/**
 * IndividualMetricInsightsContainer - Logic for individual metric insights
 *
 * Manages metric selection state and prepares chart data for display.
 * Fetches pre-computed team and position averages from the server via
 * `getSeasonMetricAverages`, which returns SQL-level aggregates per season.
 * The player's own per-season scores come from `seasonCategoricalScores`.
 *
 * When a `comparisonPlayer` is provided (from "Compare VS > Players" selection),
 * their seasonal scores are merged into the chart as an orange (#f97316) line.
 * The comparison player's `seasonCategoricalScores` use the same `graphqlField`
 * key as the main player, so extraction reuses the same field-based lookup.
 *
 * Uses `formatPositionLabel` to build grammatically correct legend labels,
 * filtering null/undefined position abbreviations from the GraphQL response.
 *
 * Uses `resolveSafeMetric` to prevent crashes during player navigation transitions
 * where `isGoalkeeper` updates but `selectedMetric` still holds the previous player's
 * value in the same render cycle, causing a config lookup mismatch.
 *
 * The descriptive text block (score-based narratives like "solid distribution
 * skills") was removed in SE-3906 because it was a leftover from career metrics
 * and no longer contextually accurate for individual metric insights.
 * @remarks This is a Container component - handles business logic and state
 */

import { ChevronDown } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";
import resolveConfig from "tailwindcss/resolveConfig";

import IndividualMetricInsightsView from "./IndividualMetricInsightsView";
import { resolveSafeMetric } from "@/features/player-detail/utils/resolveSafeMetric";
import { resolveSeasonTickValues } from "@/features/player-detail/utils/metricInsightsAxisTicks";

import {
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  useDropdownPositioning,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { METRIC_DROPDOWN_POSITIONING } from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";
import {
  DEFAULT_INDIVIDUAL_METRIC,
  DEFAULT_GK_INDIVIDUAL_METRIC,
  INDIVIDUAL_METRIC_CONFIGS,
  INDIVIDUAL_METRIC_OPTIONS,
  GK_INDIVIDUAL_METRIC_CONFIGS,
  GK_INDIVIDUAL_METRIC_OPTIONS,
  type IndividualMetricType,
  type GkIndividualMetricType,
} from "@/features/player-detail/constants";
import { useCurrentUser } from "@/features/auth";
import {
  PlayerDetailPlayerMetaFragment,
  PerformanceAnalyticsPlayerScoresFragment,
  type SeasonMetricAverages,
  usePlayerDetailGetSeasonMetricAveragesQuery,
} from "@/generated/graphql";
import { useSafeFont } from "@/hooks/useSafeFont";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { formatPositionLabel } from "@/utils/formatPositionLabel";
import { cleanSeasonScores } from "@/utils/handleDataCategoryMetrics";
import { getDashboardCardTheme } from "@/utils/radarChartUtils";
import tailwindConfig from "tailwind.config";

/** Fallback chart colors when tailwind config resolution fails */
const FALLBACK_CHART_COLORS = {
  player: "#22d3ee",
  other: "#a855f7",
  horizontalLine: "#9ca3af",
};

/**
 * Resolves chart colors from the tailwind config, falling back to hardcoded
 * defaults when the `chart` color group is missing or incomplete.
 */
function resolveChartColors(): Record<string, string> {
  const resolved = resolveConfig(tailwindConfig).theme.colors as unknown as
    | Record<string, Record<string, string>>
    | undefined;
  const chart = resolved?.chart;
  return chart?.player && chart?.other && chart?.horizontalLine
    ? chart
    : FALLBACK_CHART_COLORS;
}

/** Resolved tailwind chart colors -- evaluated once at module level */
const CHART_COLORS = resolveChartColors();

/** Dropdown option for the metric selector, shared between outfield and GK modes */
interface MetricSelectOption {
  readonly value: IndividualMetricType | GkIndividualMetricType;
  readonly label: string;
}

/** Props for {@link IndividualMetricInsightsContainer} */
interface IndividualMetricInsightsContainerProps {
  readonly player: PlayerDetailPlayerMetaFragment;
  /** Comparison player selected via "Compare VS > Players" — renders an orange line when present */
  readonly comparisonPlayer?: PerformanceAnalyticsPlayerScoresFragment | null;
}

/**
 * Merges the player's own seasonal scores with server-provided team and
 * position averages into a unified chart dataset.
 * @param seasonScores - Cleaned player seasonal categorical scores
 * @param serverAverages - Server-computed averages for the selected metric
 * @param graphqlField - Seasonal score field key (e.g. "passingTransPositionalCategoricalScore")
 * @param playerId - Current player's ID (used to build the player-specific y-key)
 * @returns Chart data array, y-keys, and color map
 */
function buildChartData(
  seasonScores: readonly Record<string, unknown>[],
  serverAverages: readonly SeasonMetricAverages[],
  graphqlField: string,
  playerId: string
): {
  data: Record<string, number>[];
  yKeys: string[];
  colors: Record<string, string>;
} {
  const currentPlayerKey = `${graphqlField}_${playerId}`;
  const positionKey = "positionAverage";
  const teamAvgKey = "teamAverage";

  const playerScoreBySeason = seasonScores.reduce<Record<number, number>>(
    (acc, entry) => {
      const season = entry.ggUnifiedSeasonKey;
      const score = entry[graphqlField];
      return typeof season === "number" && typeof score === "number"
        ? { ...acc, [season]: score }
        : acc;
    },
    {}
  );

  const allSeasons = [
    ...new Set([
      ...Object.keys(playerScoreBySeason).map(Number),
      ...serverAverages.map(a => a.season),
    ]),
  ].sort((a, b) => a - b);

  const serverAvgBySeason = serverAverages.reduce<
    Record<number, SeasonMetricAverages>
  >((acc, entry) => ({ ...acc, [entry.season]: entry }), {});

  const data: Record<string, number>[] = allSeasons.map(season => {
    const playerValue = playerScoreBySeason[season];
    const avgEntry = serverAvgBySeason[season];

    return {
      season,
      ...(typeof playerValue === "number"
        ? { [currentPlayerKey]: playerValue }
        : {}),
      ...(typeof avgEntry?.positionAverage === "number"
        ? { [positionKey]: avgEntry.positionAverage }
        : {}),
      ...(typeof avgEntry?.teamAverage === "number"
        ? { [teamAvgKey]: avgEntry.teamAverage }
        : {}),
    };
  });

  const yKeys = [currentPlayerKey, positionKey, teamAvgKey];

  const colors: Record<string, string> = {
    [currentPlayerKey]: CHART_COLORS.player,
    [positionKey]: CHART_COLORS.other,
    [teamAvgKey]: CHART_COLORS.horizontalLine,
  };

  return { data, yKeys, colors };
}

/**
 * Formats a chart x-axis value, returning the stringified integer
 * for whole numbers and an empty string for fractional values.
 * @param value - The x-axis tick value to format
 */
function formatXLabel(value: string | number): string {
  return Number.isInteger(Number(value)) ? value.toString() : "";
}

/**
 * IndividualMetricInsightsContainer manages metric selection and prepares
 * chart data from both player seasonal scores and server-provided averages.
 * @param props - Container props
 * @param props.player - Player data fragment
 * @param props.comparisonPlayer - Comparison player for orange trend line (from "Compare VS > Players")
 * @returns IndividualMetricInsightsView with computed props
 */
const IndividualMetricInsightsContainer = ({
  player,
  comparisonPlayer,
}: IndividualMetricInsightsContainerProps) => {
  const isGoalkeeper = useMemo(
    () =>
      player.positions?.some(pos =>
        pos?.name?.toLowerCase().includes("goalkeeper")
      ) ?? false,
    [player.positions]
  );

  const [selectedMetric, setSelectedMetric] = useState<
    IndividualMetricType | GkIndividualMetricType
  >(isGoalkeeper ? DEFAULT_GK_INDIVIDUAL_METRIC : DEFAULT_INDIVIDUAL_METRIC);

  // Reset selected metric when switching between GK and outfield players
  const [prevIsGoalkeeper, setPrevIsGoalkeeper] = useState(isGoalkeeper);
  if (prevIsGoalkeeper !== isGoalkeeper) {
    setPrevIsGoalkeeper(isGoalkeeper);
    setSelectedMetric(
      isGoalkeeper ? DEFAULT_GK_INDIVIDUAL_METRIC : DEFAULT_INDIVIDUAL_METRIC
    );
  }

  const font = useSafeFont(12);
  const { resolvedTheme } = useTheme();
  const currentUser = useCurrentUser();

  const isDark = resolvedTheme === "dark";
  const labelColor = isDark ? "#FFFFFF" : "#000000";

  const theme = useMemo(() => {
    const cardTheme = getDashboardCardTheme(isDark);
    return {
      containerClass: cardTheme.containerClass,
      headingClass: cardTheme.headingClass,
      textClass: "text-content-secondary",
    };
  }, [isDark]);

  const playerName = useMemo(() => player?.name ?? "", [player]);

  const positionLabel = useMemo(
    () =>
      formatPositionLabel(player.positions?.map(p => p?.abbreviation) ?? []),
    [player.positions]
  );

  const cleanedSeasonCategoricalScores = useMemo(
    () => cleanSeasonScores(player?.seasonCategoricalScores),
    [player.seasonCategoricalScores]
  );

  const teamIds = useMemo(
    () =>
      (currentUser?.organization?.organizationTeams ?? []).map(l => l.teamId),
    [currentUser?.organization?.organizationTeams]
  );

  const positionIds = useMemo(
    () =>
      (player.positions ?? []).map(p => p?.id).filter((p): p is string => !!p),
    [player.positions]
  );

  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();
  const {
    data: averagesData,
    loading: isLoading,
    error: averagesError,
  } = usePlayerDetailGetSeasonMetricAveragesQuery({
    variables: {
      teamIds,
      positionIds,
      excludePlayerId: player.id,
    },
    skip:
      !isTeamSelectionLoaded ||
      teamIds.length === 0 ||
      positionIds.length === 0,
  });

  if (averagesError) {
    console.warn("Failed to fetch season metric averages:", averagesError);
  }

  // Shell open-state + positioning (SE-5568). anchor:"right" — the trigger
  // sits at the right edge of the card header, so the panel expands leftward
  // to stay on-screen (same rationale as the SE-5551 timeframe dropdown).
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const onToggleDropdown = useCallback(
    () => setIsDropdownOpen(prev => !prev),
    []
  );
  const { triggerRef, panelGeometry, shouldShow } = useDropdownPositioning({
    isOpen: isDropdownOpen,
    ...METRIC_DROPDOWN_POSITIONING,
    anchor: "right",
  });
  const dropdown = useMemo<ShellDropdownWiring>(
    () => ({
      isOpen: isDropdownOpen,
      onToggle: onToggleDropdown,
      triggerRef,
      panelGeometry,
      shouldShow,
    }),
    [isDropdownOpen, onToggleDropdown, triggerRef, panelGeometry, shouldShow]
  );

  const createSelectHandler = useCallback(
    (option: MetricSelectOption) => () => {
      setIsDropdownOpen(false);
      setSelectedMetric(option.value);
    },
    []
  );

  const safeMetric = resolveSafeMetric(selectedMetric, isGoalkeeper);
  const metricConfig = isGoalkeeper
    ? GK_INDIVIDUAL_METRIC_CONFIGS[safeMetric as GkIndividualMetricType]
    : INDIVIDUAL_METRIC_CONFIGS[safeMetric as IndividualMetricType];

  // Metric type keys (passing, dribbling, etc.) match SeasonMetricAveragesResult fields exactly
  const serverAveragesForMetric = useMemo(() => {
    const result = averagesData?.getSeasonMetricAverages;
    if (!result) return [];
    return result[safeMetric] ?? [];
  }, [averagesData, safeMetric]);

  const chartData = useMemo(
    () =>
      buildChartData(
        cleanedSeasonCategoricalScores,
        serverAveragesForMetric,
        metricConfig.graphqlField,
        player.id
      ),
    [
      cleanedSeasonCategoricalScores,
      serverAveragesForMetric,
      metricConfig.graphqlField,
      player.id,
    ]
  );

  // Extract comparison player's seasonal scores for the selected metric
  const comparisonChartLine = useMemo(() => {
    if (!comparisonPlayer?.seasonCategoricalScores) return null;

    const cleaned = (comparisonPlayer.seasonCategoricalScores ?? []).map(obj =>
      Object.fromEntries(
        Object.entries(obj ?? {}).filter(
          ([key, value]) =>
            value !== null && key !== "__typename" && key !== "seasonId"
        )
      )
    );

    const compPlayerKey = `${metricConfig.graphqlField}_compare`;

    const scoreBySeason = cleaned.reduce<Record<number, number>>(
      (acc, entry) => {
        const season = entry.ggUnifiedSeasonKey;
        const score = entry[metricConfig.graphqlField];
        return typeof season === "number" && typeof score === "number"
          ? { ...acc, [season]: score }
          : acc;
      },
      {}
    );

    return { key: compPlayerKey, scoreBySeason };
  }, [comparisonPlayer, metricConfig.graphqlField]);

  // Merge comparison player data into chart when available
  const mergedChartData = useMemo(() => {
    if (!comparisonChartLine) return chartData;

    const { key, scoreBySeason } = comparisonChartLine;

    const enrichedData = chartData.data.map(point => {
      const season = point.season as number;
      const compScore = scoreBySeason[season];
      return typeof compScore === "number"
        ? { ...point, [key]: compScore }
        : point;
    });

    // Add seasons the comparison player has but the main player doesn't
    const existingSeasons = new Set(
      chartData.data.map(d => d.season as number)
    );
    const extraPoints = Object.entries(scoreBySeason)
      .filter(([s]) => !existingSeasons.has(Number(s)))
      .map(([s, score]) => ({ season: Number(s), [key]: score }));

    const allData = [...enrichedData, ...extraPoints].sort(
      (a, b) => (a.season as number) - (b.season as number)
    );

    return {
      data: allData,
      yKeys: [...chartData.yKeys, key],
      colors: { ...chartData.colors, [key]: "#f97316" },
    };
  }, [chartData, comparisonChartLine]);

  const comparisonPlayerName = useMemo(
    () => comparisonPlayer?.name ?? null,
    [comparisonPlayer]
  );

  /**
   * The tick ceiling handed to victory-native, which runs
   * `downsampleTicks(tickValues, tickCount)` and thins the ticks when it has
   * more values than this. Kept equal to the season count so the downsample
   * is a no-op; `xTickValues` below is what actually places the ticks.
   */
  const xTickCount = mergedChartData.data.length;

  /**
   * The season years themselves, pinned as explicit x-axis tick positions.
   *
   * SE-7139: a tick *count* alone leaves the positions to d3-scale, which picks
   * round numbers across the domain rather than the seasons actually plotted.
   * For unevenly spaced seasons it coarsens the step and skips the most recent
   * one, and victory-native draws a label only where it placed a tick — so that
   * season's point rendered with no year beneath it.
   */
  const xTickValues = useMemo(
    () => resolveSeasonTickValues(mergedChartData.data),
    [mergedChartData.data]
  );

  const yKeyLabels = useMemo(() => {
    const currentPlayerKey = `${metricConfig.graphqlField}_${player.id}`;
    const baseLabels: Record<string, string> = {
      [currentPlayerKey]: playerName,
      positionAverage: positionLabel,
      teamAverage: "Team Average",
    };
    return comparisonChartLine && comparisonPlayerName
      ? { ...baseLabels, [comparisonChartLine.key]: comparisonPlayerName }
      : baseLabels;
  }, [
    metricConfig.graphqlField,
    player.id,
    playerName,
    positionLabel,
    comparisonChartLine,
    comparisonPlayerName,
  ]);

  const metricOptions: readonly MetricSelectOption[] = useMemo(
    () =>
      isGoalkeeper
        ? GK_INDIVIDUAL_METRIC_OPTIONS.map(
            (type: GkIndividualMetricType): MetricSelectOption => ({
              value: type,
              label: GK_INDIVIDUAL_METRIC_CONFIGS[type].label,
            })
          )
        : INDIVIDUAL_METRIC_OPTIONS.map(
            (type: IndividualMetricType): MetricSelectOption => ({
              value: type,
              label: INDIVIDUAL_METRIC_CONFIGS[type].label,
            })
          ),
    [isGoalkeeper]
  );

  const selectedOptionLabel = useMemo(
    () => metricOptions.find(o => o.value === selectedMetric)?.label ?? "",
    [metricOptions, selectedMetric]
  );

  const pillClasses =
    "flex-row items-center gap-1 rounded-sm bg-surface-raised px-2 py-1";

  // Shell-hosted trigger (replaces the gluestack Menu trigger render prop)
  const trigger = useMemo(
    () => (
      <FixedPositionDropdownShellTrigger
        ref={
          triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>
        }
        isOpen={isDropdownOpen}
        onPress={onToggleDropdown}
        accessibilityLabel={`Select metric, currently ${selectedOptionLabel}`}
        className={pillClasses}
      >
        <Stack direction="horizontal" className="items-center gap-1">
          <Text
            variant="body-strong"
            className={`${isDark ? "text-white" : "text-content-secondary"}`}
          >
            {selectedOptionLabel}
          </Text>
          <Icon
            as={ChevronDown}
            size="sm"
            className={isDark ? "text-white" : "text-content-muted"}
          />
        </Stack>
      </FixedPositionDropdownShellTrigger>
    ),
    [
      pillClasses,
      isDark,
      isDropdownOpen,
      onToggleDropdown,
      selectedOptionLabel,
      triggerRef,
    ]
  );

  return (
    <IndividualMetricInsightsView
      isDark={isDark}
      selectedMetric={safeMetric}
      metricOptions={metricOptions}
      createSelectHandler={createSelectHandler}
      dropdown={dropdown}
      trigger={trigger}
      formatXLabel={formatXLabel}
      chartData={mergedChartData.data}
      yKeys={mergedChartData.yKeys}
      colors={mergedChartData.colors}
      yKeyLabels={yKeyLabels}
      font={font}
      labelColor={labelColor}
      playerName={playerName}
      positionLabel={positionLabel}
      xTickCount={xTickCount}
      xTickValues={xTickValues}
      isLoading={isLoading}
      theme={theme}
      comparisonPlayerName={comparisonPlayerName}
      legendColors={{
        player: CHART_COLORS.player,
        position: CHART_COLORS.other,
        teamAverage: CHART_COLORS.horizontalLine,
      }}
    />
  );
};

IndividualMetricInsightsContainer.displayName =
  "IndividualMetricInsightsContainer";

export default React.memo(IndividualMetricInsightsContainer);
